import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useSelector} from 'react-redux';
import {useCallback, useEffect, useRef, useState} from 'react';
import Typed from 'typed.js';
import {
  Box,
  Button,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {AnimatePresence, motion} from 'framer-motion';

import {BgCurve, Mobile} from '@jobseeker/lotus/assets/images';

import {
  handleTracking,
  openUrl,
  TrackEventUBA,
  deferTask,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';

import {
  getRefTagAndOrigin,
  HomeCategory,
} from '@components/HomeV2/homeCategory';

import {words} from '@components/HomeV2/constants';
import HomePageFooter from '@components/HomeV2/homePageFooter';
import CompanyJobListingSeoLinkages from '@components/HomeV2/companySeo';
import {Static} from '@components/HomeV2/static';
import HomePageCompanyIcons from '@components/HomeV2/homePageCompanyJobs';
import HomePageJobs from '@components/HomeV2/homePageJobs';
import {JobSearchBar} from '@components/HomeV2/homePageSearch';
import dynamic from 'next/dynamic';
import {Loader} from '@jobseeker/lotus/components';
import {HamburgerMenuNew} from '@jobseeker/lotus/assets/icons';
import MenuDrawer from '@jobseeker/lotus/components/MenuDrawer';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {HOME} from '@constants';
import WomensDaySpecial from '@components/HomeV2/womensDaySpecial';

import type {AppState} from '@jobseeker/common-core/types';
import {getKeyFromPath} from '@utils';
import TalentBridgePromoSlider from '../TalentBridge/TalentBridgePromoSlider';

const Img = styled(Image)(({theme}) => ({
  position: 'absolute',
  left: 0,
  width: '100%',
  top: 0,
  height: 705,
  zIndex: -1,

  [theme.breakpoints.up('lg')]: {
    height: 770, // min-width: 1280px
  },
  [theme.breakpoints.down('md')]: {
    height: 1023, // max-width: 1023px
  },
  [theme.breakpoints.down('sm')]: {
    height: 650, // max-width: 430px
  },
}));

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);

const Text = styled(Typography)(({theme}) => ({
  textAlign: 'center',
  margin: '0 auto',
  marginTop: '15px',
  marginBottom: '0px',
  lineHeight: '57px',
  fontSize: '35px',
  fontWeight: 600,
  color: '#000000',
  width: '793px',
  fontFamily: 'Nunito Sans',
  [theme.breakpoints.down(1023)]: {
    fontSize: '40px',
    lineHeight: 1.23,
    width: '78%',
    marginBottom: '31px',
    minHeight: '98px',
  },
  [theme.breakpoints.down(599)]: {
    width: '78%',
  },
  [theme.breakpoints.between(425, 599)]: {
    minHeight: '147px',
    marginTop: '30px',
  },
  [theme.breakpoints.down(484)]: {
    fontSize: '26px',
    marginTop: '30px',
    marginBottom: '10px',
    minHeight: '93px',
    width: '88%',
  },
}));

const CookieBanner = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/NavigationHarness/CookieBanner'
    ),
  {
    loading: () => <Loader />,
  }
);

const PageContentWrapper = styled('div')(({theme}) => ({
  width: '100%',
  height: '100%',

  zIndex: 5,
  position: 'relative',
  top: 0,
  marginBottom: true ? '0px' : '0',
  [theme.breakpoints.up('md')]: {
    marginBottom: '0px',
  },

  [theme.breakpoints.up('md')]: {
    '&::before': {
      height: '450px', // Adjust for larger screens
    },
  },
}));

const HomeV2 = ({
  blogs,
  sortedJobs,
  autoReg = false,
}: {
  blogs: object;
  sortedJobs: Array<object>;
  autoReg?: boolean;
}) => {
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [enableSignIn, setEnableSignIn] = useState<boolean>(false);
  const searchFormValue = useSelector((state: AppState) => state.job.filters);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openMenuDrawer, setMenuDrawerOpen] = useState(false);

  const handleMenuDrawer = useCallback((open: boolean) => {
    deferTask(() => {
      setMenuDrawerOpen(open);
    });
  }, []);

  const handleMenuDrawerLogin = useCallback(() => {
    setEnableSignIn(true);
    setLoginOpen(true);
  }, []);

  const handleMenuDrawerLogout = useCallback(() => {}, []);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const interceptor: any = useInterceptor();

  const {
    header: {
      meta: {tabsList},
    },
  } = interceptor;

  const userId = '-1';
  const [url, setUrl] = useState(process.env.NEXT_PUBLIC_LOAD_CONFIG);
  const router = useRouter();

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const urlObj = new URL(url ?? '');
  const queryParams = Object.fromEntries(urlObj.searchParams.entries());

  useEffect(() => {
    if (router?.pathname?.includes('/register')) {
      TrackEventUBA('registrationInitialOverlayPageView', 'view', {
        ref: queryParams.ref || '',
        sessionUsername: userId ? userId.toString() : '',
        pageName: 'Register Campaign',
      });
    }
  }, []);

  const handleUbaTracking = (eventName: string, section: string) => {
    TrackEventUBA(eventName, 'click', {
      page: 'jsHomePage',
      section,
      ref: queryParams.ref || '',
      sessionUsername: userId ? userId.toString() : '',
    });
  };

  const handleLogoClick = () => {
    window.location.href = '/';
    deferTask(() => {
      const {origin, refText} = getRefTagAndOrigin();
      handleTracking('jsClickMainLogo', {
        event_category: 'jsHomePage',
        Source: `${origin}`,
        Device: sessionStorage.getItem('device') || 'desktop',
        Ref: `${refText}`,
      });
      handleUbaTracking('jsHomePageClick', 'hiristLogo');
    });
  };

  const recruiterLogin = () => {
    deferTask(() => {
      const {origin, refText} = getRefTagAndOrigin();
      handleTracking(
        'jsClickRecruiterLogin',

        {
          event_category: 'jsHomePage',
          Source: `${origin}`,
          Device: sessionStorage.getItem('device') || 'desktop',
          Ref: `${refText}`,
        }
      );
      handleUbaTracking('jsHomePageClick', 'RecruiterLogin');
      openUrl(
        `https://recruit${process.env.NEXT_PUBLIC_DOMAIN}/login`,
        '_blank'
      );
    });
  };

  const userLogin = () => {
    deferTask(() => {
      const {origin, refText} = getRefTagAndOrigin();
      handleTracking(
        'jsClickJobseekerLogin',

        {
          event_category: 'jsHomePage',
          Source: `${origin}`,
          Device: sessionStorage.getItem('device') || 'desktop',
          Ref: `${refText}`,
        }
      );
      handleUbaTracking('jsHomePageClick', 'JobseekerLogin');
      TrackEventUBA('jsLoginLogoView', 'view', {
        page: 'jsHomePage',
        section: 'signup',
        ref: queryParams.ref || '',
        sessionUsername: userId ? userId.toString() : '',
      });
      TrackEventUBA('loginPageView', 'view', {
        ref: getKeyFromPath('ref') || '',
        pageName: getUbaPageName(),
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
      setEnableSignIn(true);
      setLoginOpen(true);
      // this.props.openLoginPopup();
    });
  };

  const userRegister = () => {
    deferTask(() => {
      const {origin, refText} = getRefTagAndOrigin();
      handleTracking(
        'jsClickJobseekerRegister',

        {
          event_category: 'jsHomePage',
          Source: `${origin}`,
          Device: sessionStorage.getItem('device') || 'desktop',
          Ref: `${refText}`,
        }
      );
      handleUbaTracking('jsHomePageClick', 'Register');
      TrackEventUBA('registrationInitialPageView', 'view', {
        ref: queryParams.ref || '',
        sessionUsername: userId ? userId.toString() : '',
        pageName: getUbaPageName(),
      });
      setEnableSignIn(false);
      setLoginOpen(true);
    });
  };

  const onAppDownloadClick = () => {
    deferTask(() => {
      handleTracking(
        'jsClickDwdAppBeforeLogin',

        {
          event_category: 'MobileAppPromotion',
          Origin: 'LandingPage',
          Device: sessionStorage.getItem('device') || 'desktop',
          Status: 'LoggedOut',
        }
      );
      handleUbaTracking('jsHomePageClick', 'DownloadApp');
      openUrl('/downloadapp?ref=landingpage', '_self');
    });
  };

  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const cookieBannerData = {
    policyLinks: {
      privacyPolicy: 'https://www.iimjobs.com/privacy/',
      cookiePolicy: 'https://www.iimjobs.com/cookiepolicy',
    },
    CTA: 'Got it',
  };
  const el = useRef<HTMLSpanElement>(null); // Ref for the DOM element
  const typedInstance = useRef<Typed | null>(null);
  const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';
  useEffect(() => {
    if (!words?.length) return; // Wait until words are available

    const timer = setTimeout(() => {
      if (el.current) {
        el.current.innerText = words[0];

        typedInstance.current?.destroy();
        typedInstance.current = new Typed(el.current, {
          strings: words,
          showCursor: true,
          typeSpeed: 60,
          backSpeed: 60,
          loop: true,
        });
      }
    }, 500); // Small delay to ensure mounting

    return () => {
      clearTimeout(timer);
      typedInstance.current?.destroy();
    };
  }, [words]);

  return (
    <>
      {isAppHirist && <TalentBridgePromoSlider />}

      {(loginOpen || autoReg) && (
        <HiristAuthentication
          variant={VersionVariants.VERSION_TWO}
          openDialog={loginOpen}
          setOpenDialog={() => {
            setLoginOpen(false);
          }}
          enableSignIn={enableSignIn}
        />
      )}

      <PageContentWrapper>
        <Img src={BgCurve as unknown as string} alt="background image" />

        <Box
          sx={{
            padding: {xxs: '18px 0px 10px 15px', md: '22px 70px 0px'},
            position: {xxs: 'sticky', md: 'static'},
            top: 0,
            zIndex: 50,
            backgroundColor: {xxs: '#FEFAF0', md: 'transparent'},
          }}
          display="flex"
          alignItems="center"
          justifyContent={{xxs: 'flex-start', md: 'space-between'}}
        >
          <Box display="flex">
            <Box
              display={{xxs: 'flex', lg: 'none'}}
              mr={theme.spacing(2)}
              alignItems="center"
              onClick={() => {
                handleMenuDrawer(true);
              }}
            >
              <Image
                src={HamburgerMenuNew}
                alt={'HamburgerMenu'}
                data-testid="menu_mobile"
                data-name="menu_mobile"
                width={24}
                height={24}
                objectFit="cover"
                quality={50}
                style={{marginRight: 0.5, verticalAlign: 'top'}}
              />
            </Box>
            <MenuDrawer
              theme={theme}
              handleDrawer={handleMenuDrawer}
              openDrawer={openMenuDrawer}
              name={''}
              email={''}
              phoneVerified={false}
              profileProgress={98}
              profilePicUrl={''}
              proMembership={undefined}
              OnLogoutBtnClick={handleMenuDrawerLogout}
              followUps={undefined}
              profileDrawerOptions={undefined}
              tabsList={tabsList}
              aboutSection={[]}
              proMeta={{refCode: '', refFeature: ''}}
              reCap={0}
              page={HOME}
              savedJobCount={undefined}
              sendEvent={undefined}
              loginHandler={handleMenuDrawerLogin}
              showSpotLight={false}
            />
            <a
              href={`/`}
              style={{
                textDecoration: 'none',
              }}
            >
              <Image
                src="https://hirist-assets.s3.ap-south-1.amazonaws.com/hirist-tech-logo-new.webp"
                alt="HIRIST.TECH"
                className="sc-kEYyzF iPqUTn"
                height={isMobile ? 32 : 40}
                width={isMobile ? 110 : 150}
                onClick={handleLogoClick}
                style={{
                  cursor: 'pointer',
                  marginTop: 5,
                }}
              />
            </a>
          </Box>
          <Box
            display={{xxs: 'flex', md: 'none'}}
            alignItems="center"
            gap={1}
            flexGrow={1}
            justifyContent="flex-end"
            pr="15px"
          >
            <Button
              onClick={userLogin}
              sx={{
                width: 80,
                height: 41,
                backgroundColor: '#FEFAF0',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '8px',
                minWidth: 'unset',
                color: theme.palette.primary.main,
                padding: 0,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Login
              </Typography>
            </Button>
            <Button
              onClick={userRegister}
              sx={{
                width: 80,
                height: 41,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '8px',
                minWidth: 'unset',
                color: 'white',
                padding: 0,
                border: `1px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Register
              </Typography>
            </Button>
          </Box>

          <Box display={{xxs: 'none', md: 'flex'}} alignItems="center">
            <Button
              onClick={onAppDownloadClick}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Image src={Mobile} alt="down" height={24} width={16} />

              <Typography
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  marginLeft: 1,
                }}
              >
                Download App
              </Typography>
            </Button>

            <Button
              onClick={userLogin}
              sx={{
                width: 110,
                height: 50,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '8px',
                marginLeft: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                transition: 'background-color 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  fontWeight: 600,
                  transition: 'color 0.3s ease-in-out',
                  textTransform: 'none',
                }}
              >
                Login
              </Typography>
            </Button>

            <Button
              onClick={userRegister}
              sx={{
                width: 110,
                height: 50,
                color: 'white',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '8px',
                marginLeft: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.3s ease-in-out',
                border: `1px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  fontWeight: 600,
                  transition: 'color 0.3s ease-in-out',
                  textTransform: 'none',
                }}
              >
                Register
              </Typography>
            </Button>

            <Box
              sx={{
                width: 2,
                height: 21,
                backgroundColor: '#EAEAEA',
                mx: 2,
              }}
            />

            <Button
              onClick={recruiterLogin}
              sx={{
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Nunito Sans',
                  fontSize: 16,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Login as Recruiter
              </Typography>
            </Button>
          </Box>
        </Box>
        {device == 'desktop' ? (
          <Text
            style={{
              fontWeight: '600',
              fontFamily: 'Nunito Sans',
            }}
          >
            Handpicked Premium{' '}
            <b
              style={{
                fontWeight: '600',
                color: theme.palette.primary.main,
                fontFamily: 'Nunito Sans',
              }}
              ref={el}
            />
            {' Jobs'}
          </Text>
        ) : (
          <Text>
            Handpicked Premium <br />
            <b
              style={{
                fontWeight: '600',
                color: theme.palette.primary.main,
                fontFamily: 'Nunito Sans',
              }}
            >
              <b
                style={{
                  fontWeight: '600',
                  color: theme.palette.primary.main,
                  fontFamily: 'Nunito Sans',
                }}
                ref={el}
              />
            </b>
            <br /> {' Jobs'}
          </Text>
        )}
        <Box
          width={{xxs: '87%', md: '75%', lg: '55%'}}
          sx={{
            backgroundColor: {md: '#fff'},
            p: {md: 1.5},
            pb: {md: 1},
            pr: {md: 2.5},
            borderRadius: {md: 2},
            boxShadow: {md: '0 10px 50px 0 rgba(0,0,0,0.07)'},
            border: {xxs: 0, md: 1},
            borderColor: '#ebebeb !important',
          }}
          alignItems="center"
          mx="auto"
          mb="48px"
          mt="12px"
        >
          <JobSearchBar
            variant={VersionVariants.VERSION_TWO}
            showSortBy={false}
            isFilter={false}
            showSearchInput={true}
            formValue={searchFormValue}
            inline={true}
            onlySeachInput={false}
            industryOptions={[]}
          />
        </Box>
        <HomeCategory loginOpen={loginOpen} />
        <AnimatePresence>
          <motion.div
            key="wow-banner"
            initial={{opacity: 0, y: -20}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -20}}
            transition={{duration: 0.6, ease: 'easeOut'}}
          >
            <WomensDaySpecial />
          </motion.div>
        </AnimatePresence>
        <HomePageCompanyIcons isTechDrive={false} />

        <HomePageJobs sortedJobs={sortedJobs} />
        <CompanyJobListingSeoLinkages source="homepage" />

        <Static
          isTechDrive={false}
          setLoginPopUp={value => {
            setEnableSignIn(false);
            setLoginOpen(value);
          }}
          blogs={blogs}
        />
        <HomePageFooter />
      </PageContentWrapper>
      <CookieBanner
        showCookieBanner={showCookieBanner}
        setShowCookieBanner={setShowCookieBanner}
        policyLinks={cookieBannerData.policyLinks}
        CTA={cookieBannerData.CTA}
      />
    </>
  );
};

export default HomeV2;
